'use client';

import { useState } from 'react';

import { FcGoogle } from 'react-icons/fc';

import FormEmailAndPasswordBetterAuth from '@/features/auth/components/FormEmailAndPasswordBetterAuth';
import { getPartner } from '@/features/v2/auth/register/actions/getPartner';
import { useAffiliateFromUrl } from '@/features/v2/auth/register/hooks/useAffiliateFromUrl';
import { useGoogleRegister } from '@/features/v2/auth/register/hooks/useGoogleRegister';
import { useOtpSheet } from '@/features/v2/auth/register/hooks/useOtpSheet';
import { useRegistration } from '@/features/v2/auth/register/hooks/useRegistration';
import { core } from '@/features/v2/landings/register/components/localization/core';

import { Vendors } from '@/enums/core';

import { OtpVerificationSheet } from '@/components/sheet/OtpVerificationSheet';
import { useDetectCountry } from '@/src/queries/country/use-detect-country';

export type RegisterMethodsProps = {
  welcomeBonus: string;
  /** Optional parent UI signal (e.g. landing full-page spinner during Google OAuth). */
  onGooglePendingChange?: (pending: boolean) => void;
};

export const RegisterMethods = ({
  welcomeBonus,
  onGooglePendingChange,
}: RegisterMethodsProps) => {
  const affiliateFromUrl = useAffiliateFromUrl();
  const [affiliate, setAffiliate] = useState(() => ({
    open: Boolean(affiliateFromUrl),
    code: affiliateFromUrl ?? '',
  }));

  const { data } = useDetectCountry();
  const detectedCountryCode = data?.countryCode ?? 'GLOBAL';
  const [selectedCountryCode, setSelectedCountryCode] = useState(detectedCountryCode);

  const partner = getPartner(affiliate.code);

  const {
    isOtpSheetOpen,
    registeredEmail,
    openOtpSheet,
    closeOtpSheet,
    handleOtpVerificationSuccess,
  } = useOtpSheet();

  const {
    isCollectingFingerprint,
    collectVisitorIdBeforeRegister,
    handleRegistrationSuccessWithOtp,
    handleRegistrationError,
  } = useRegistration({
    welcomeBonus,
    openOtpSheet,
  });

  const {
    state: googleRegisterState,
    handleGoogleRegister,
  } = useGoogleRegister({
    welcomeBonus,
    affiliateCode: affiliate.code,
    partner,
    selectedCountryCode,
    onPendingChange: onGooglePendingChange,
  });

  // Aggregate Google-button pending here; email submit locks stay inside the form.
  const isGoogleButtonDisabled =
    isCollectingFingerprint || googleRegisterState === 'pending';

  return (
    <section className="flex flex-col mt-6 mx-4 w-full pb-8 relative z-20">
      <FormEmailAndPasswordBetterAuth
        isRegister
        onSuccessWithOtp={handleRegistrationSuccessWithOtp}
        onError={handleRegistrationError}
        onBeforeRegister={collectVisitorIdBeforeRegister}
        country={selectedCountryCode}
        onCountryChange={setSelectedCountryCode}
        gift={welcomeBonus}
        parent={Number(affiliateFromUrl) || 0}
        affiliatorId={affiliate.code}
        partner={partner || ''}
        vendor={Vendors.EMAIL}
      />

      <OtpVerificationSheet
        isOpen={isOtpSheetOpen}
        onClose={closeOtpSheet}
        email={registeredEmail}
        onSuccess={handleOtpVerificationSuccess}
      />

      <div className="flex flex-col items-center justify-center gap-2 mt-2 pb-36">
        <div className="flex items-center gap-5">
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              aria-label={core.google}
              onClick={handleGoogleRegister}
              disabled={isGoogleButtonDisabled}
              className="bg-white rounded-full w-10 h-10 flex items-center justify-center outline-hidden focus:outline-hidden focus:border-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FcGoogle className="text-2xl" aria-hidden />
            </button>
            <p>{core.google}</p>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-[10px] mt-3">
          <p
            className="text-base underline font-extralight"
            onClick={() => {
              setAffiliate((prev) => ({
                ...prev,
                open: !prev.open,
              }));
            }}
          >
            ¿Tienes un código de afiliado?
          </p>
          {affiliate.open && (
            <input
              type="text"
              placeholder="Código de afiliado"
              className="inputsV2Light"
              value={affiliate.code}
              onChange={(e) => {
                setAffiliate((prev) => ({
                  ...prev,
                  code: e.target.value,
                }));
              }}
            />
          )}
        </div>
      </div>
    </section>
  );
};
